// FILE: stratify_website/src/pages/PrivacyPolicyPage.jsx
import React from "react";
import { SITE } from "../config/site";
import { Meta } from "../layout/EducationMeta";
import GlassCard from "../components/ui/GlassCard";
import Link from "../components/Link";
import { LEGAL_SHARED, relatedLegalLinks } from "../config/legal";

const PAGE =
  "mx-auto w-full max-w-[110rem] px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 border-t border-ink/8 pt-8 first:border-t-0 first:pt-0">
      <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-ink">{title}</h2>
      <div className="mt-4 space-y-4 text-sm md:text-[15px] leading-relaxed text-muted">
        {children}
      </div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-hint-strong" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function PrivacyPolicyPage() {
  const canonical = SITE.privacyPolicyUrl;

  const privacySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Privacy Policy — Stratify Backtesting",
    url: canonical,
    description:
      "Privacy Policy for Stratify Backtesting, including account data, Firebase services, strategy notifications, app analytics, and data deletion requests.",
    publisher: {
      "@type": "Organization",
      name: SITE.legalName,
      url: SITE.url,
      email: SITE.supportEmail,
    },
  };

  return (
    <div className={`${PAGE} py-12 md:py-16`}>
      <Meta
        title="Privacy Policy — Stratify Backtesting"
        description="Privacy Policy for Stratify Backtesting, including account data, Firebase services, app analytics, notifications, and privacy requests."
        canonical={canonical}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(privacySchema) }}
      />

      <div className="max-w-4xl">
        <div className="inline-flex items-center rounded-full border border-hint/25 bg-hint/10 px-3 py-1 text-xs font-semibold tracking-wide text-hint-deep">
          Privacy Policy
        </div>
        <h1 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight text-ink">
          Privacy Policy
        </h1>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-muted">
          This Privacy Policy explains how {SITE.legalName} collects, uses, stores,
          and protects information when you use {SITE.appName} and the Stratify
          Backtesting website.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-muted">
          {LEGAL_SHARED.billingDraftNote}
        </p>
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
            Effective date
          </div>
          <div className="mt-2 text-lg font-semibold text-ink">May 26, 2026</div>
        </GlassCard>
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
            Company
          </div>
          <div className="mt-2 text-lg font-semibold text-ink">{SITE.legalName}</div>
        </GlassCard>
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
            Contact
          </div>
          <a
            href={`mailto:${SITE.supportEmail}`}
            className="mt-2 inline-flex break-all text-lg font-semibold text-hint-deep hover:text-ink transition"
          >
            {SITE.supportEmail}
          </a>
        </GlassCard>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <GlassCard className="sticky top-6 p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
              On this page
            </div>
            <nav className="mt-4 space-y-2 text-sm">
              {[
                ["Introduction", "introduction"],
                ["Information we collect", "information-we-collect"],
                ["How we use information", "how-we-use-information"],
                ["Storage and security", "storage-security"],
                ["Third-party services", "third-party-services"],
                ["Retention and deletion", "retention-deletion"],
                ["Children's privacy", "children"],
                ["No financial advice", "no-financial-advice"],
                ["Website billing (draft)", "website-billing"],
                ["Your rights", "your-rights"],
                ["Contact", "contact"],
              ].map(([label, id]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="block rounded-lg px-3 py-2 text-muted hover:bg-bg1 hover:text-ink transition"
                >
                  {label}
                </a>
              ))}
            </nav>
          </GlassCard>
        </aside>

        <main className="lg:col-span-9">
          <GlassCard className="p-6 md:p-8">
            <div className="space-y-8">
              <Section id="introduction" title="1. Introduction">
                <p>
                  Thank you for using Stratify. Stratify is a stock market paper
                  trading simulator, strategy wallet, and backtesting tool. We are
                  committed to protecting your privacy and handling your information
                  responsibly.
                </p>
                <p>
                  By using the app or website, you agree to the practices described
                  in this Privacy Policy. If you do not agree, do not use Stratify.
                </p>
              </Section>

              <Section id="information-we-collect" title="2. Information we collect">
                <h3 className="text-base font-semibold text-ink">A. Account information</h3>
                <p>When you create or use a Stratify account, we may collect:</p>
                <BulletList
                  items={[
                    "Name",
                    "Email address",
                    "Encrypted authentication credentials managed through Firebase Authentication",
                    "Device notification token if you enable notifications",
                  ]}
                />
                <p>
                  We do not collect sensitive payment card numbers, bank account
                  numbers, brokerage account credentials, or bank login details.
                </p>
                <p>
                  For authorized website purchases, Stripe may collect card details.
                  Stratify may receive subscription and payment metadata such as
                  customer and transaction identifiers, plan, currency, subscription
                  status, and payment outcome. Stratify does not store full card numbers.
                  Website signup checkboxes are a frontend gate only and are not stored
                  as a billing-consent receipt.
                </p>

                <h3 className="pt-3 text-base font-semibold text-ink">B. Usage and performance information</h3>
                <p>We may collect limited app usage and performance information, including:</p>
                <BulletList
                  items={[
                    "Feature usage and interaction patterns",
                    "Session and general app performance data",
                    "Crash, diagnostic, and error information used to improve app reliability",
                    "Basic device and technical information needed to maintain the app",
                  ]}
                />

                <h3 className="pt-3 text-base font-semibold text-ink">C. Trading, paper trading, and strategy data</h3>
                <p>
                  Stratify is designed so core paper trading and backtesting workflows
                  can remain local on your device unless cloud features are enabled.
                  Local data may include paper trading transactions, portfolio values,
                  strategies, backtesting configurations, and simulation results.
                </p>
                <p>
                  If you enable strategy notifications or cloud monitoring, limited
                  strategy-related information may be stored securely in Firebase so
                  Stratify can evaluate the selected strategy and send notification
                  updates. This may include the selected stock symbol, strategy rules,
                  indicator values needed to evaluate the strategy, notification state,
                  and a device notification token.
                </p>
              </Section>

              <Section id="how-we-use-information" title="3. How we use information">
                <p>We use information for the following purposes:</p>
                <BulletList
                  items={[
                    "To create, authenticate, and manage your account",
                    "To provide core app functionality, including strategy monitoring and notifications when enabled",
                    "To improve app performance, reliability, design, and user experience",
                    "To troubleshoot technical issues and maintain app security",
                    "To respond to support, account, privacy, and deletion requests",
                    "To maintain purchase, entitlement, and subscription access where applicable",
                  ]}
                />
                <p>
                  We do not sell, rent, or trade your personal information. We do not
                  use your personal information to provide financial, investment, or
                  trading advice.
                </p>
              </Section>

              <Section id="storage-security" title="4. Data storage and security">
                <BulletList
                  items={[
                    "Account authentication is handled through Firebase Authentication.",
                    "Strategy notification data, when enabled, may be stored in Firebase Firestore.",
                    "Push notification delivery may use Firebase Cloud Messaging.",
                    "Some app analytics and diagnostic information may be processed through Firebase services.",
                    "Local portfolio, paper trading, and backtesting data may be stored on your device.",
                  ]}
                />
                <p>
                  We use reasonable technical and organizational safeguards to protect
                  information. However, no method of transmission or storage is 100%
                  secure, and we cannot guarantee absolute security.
                </p>
              </Section>

              <Section id="third-party-services" title="5. Third-party services">
                <p>Stratify may use third-party services to operate the app, including:</p>
                <BulletList
                  items={[
                    "Firebase Authentication for secure account login",
                    "Firebase Analytics and diagnostics for app improvement and reliability",
                    "Firebase Firestore for cloud-stored strategy notification data when notifications are enabled",
                    "Firebase Cloud Messaging for push notifications",
                    "Apple App Store services for app distribution, in-app purchases, and subscription billing",
                    "Stripe for authorized website purchases and fraud prevention (founder-draft website billing language)",
                    "Third-party market data providers for educational and simulation data",
                  ]}
                />
                <p>
                  Third-party providers may process technical information such as IP
                  address, device identifiers, diagnostics, and usage data according to
                  their own policies and the services they provide to Stratify.
                </p>
              </Section>

              <Section id="retention-deletion" title="6. Data retention and deletion">
                <BulletList
                  items={[
                    "Account information is retained while your account remains active.",
                    "Local app data can be removed by deleting it inside the app where available or uninstalling the app from your device.",
                    "Cloud strategy notification data is retained while notifications or monitoring are enabled and may be removed when you delete your account or request deletion.",
                    "Support emails may be retained as needed to resolve your request, maintain records, and protect the security of the service.",
                  ]}
                />
                <p>
                  To request deletion of account-related personal information, email
                  support from the email address connected to your account or include
                  enough information for us to identify the account.
                </p>
                <p>
                  Deleting the app or asking us to delete an account does not by itself
                  cancel an Apple or Stripe subscription. Cancel billing separately,
                  then email a Privacy Request if you want the account removed. We do
                  not publish a numeric retention schedule or a deletion SLA.
                </p>
              </Section>

              <Section id="children" title="7. Children's privacy">
                <p>
                  Stratify is intended for users who are at least 13 years old. We do
                  not knowingly collect personal information from children under 13.
                  If we learn that we have collected personal information from a child
                  under 13, we will take reasonable steps to delete it.
                </p>
              </Section>

              <Section id="no-financial-advice" title="8. Educational use only — no financial advice">
                <p>
                  Stratify is for educational, research, simulation, paper trading, and
                  backtesting purposes only. Stratify does not provide financial,
                  investment, legal, tax, trading, or brokerage advice.
                </p>
                <p>
                  Strategy results, backtests, indicators, alerts, buy/sell labels,
                  signal changes, and notifications are educational outputs only. They
                  are not instructions or recommendations to buy, sell, hold, or trade
                  any security or financial instrument. Past performance and simulated
                  results do not guarantee future outcomes.
                </p>
              </Section>

              <Section id="website-billing" title="8A. Website billing (founder draft)">
                <p>{LEGAL_SHARED.noLiveTrading}</p>
                <p>{LEGAL_SHARED.sellers}</p>
                <p>{LEGAL_SHARED.marketing}</p>
                <p>
                  Automated live trading is not included. Brokerage holdings and order
                  data are not collected as part of the current subscriptions.
                </p>
              </Section>

              <Section id="your-rights" title="9. Your rights">
                <p>
                  Depending on your location, you may have privacy rights under
                  applicable laws, including the right to request access, correction,
                  deletion, or information about how your personal data is used.
                </p>
                <p>
                  To make a privacy request, contact us at {SITE.supportEmail}. We may
                  need to verify your identity before completing certain requests.
                </p>
              </Section>

              <Section id="changes" title="10. Changes to this Privacy Policy">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes
                  in our app, website, services, legal requirements, or privacy practices.
                  The updated policy will be posted on this page with a revised effective
                  date.
                </p>
              </Section>

              <Section id="contact" title="11. Contact us">
                <p>
                  For questions about this Privacy Policy, support requests, or privacy
                  rights requests, contact us at:
                </p>
                <div className="rounded-2xl border border-ink/8 bg-bg1 p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
                    Email
                  </div>
                  <a
                    href={`mailto:${SITE.supportEmail}`}
                    className="mt-2 inline-flex break-all text-lg font-semibold text-hint-deep hover:text-ink transition"
                  >
                    {SITE.supportEmail}
                  </a>
                  <div className="mt-4 text-xs uppercase tracking-[0.16em] text-muted">
                    Company
                  </div>
                  <div className="mt-1 font-semibold text-ink">{SITE.legalName}</div>
                </div>
              </Section>
            </div>
          </GlassCard>

          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/support"
              className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
            >
              Contact support →
            </Link>
            <Link
              to="/terms-of-use"
              className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
            >
              View terms →
            </Link>
            {relatedLegalLinks("/privacy-policy")
              .filter((item) => item.to !== "/support" && item.to !== "/terms-of-use")
              .map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
                >
                  {item.label} →
                </Link>
              ))}
          </div>
        </main>
      </div>
    </div>
  );
}
